import {
  gql,
  useMutation,
  ApolloQueryResult,
  MutationFunctionOptions,
  FetchResult,
  useQuery,
} from '@apollo/client';
import React, {useMemo} from 'react';
import {useSnackbar} from 'notistack';
import {Box, Typography} from '@mui/material';
import {
  CONTACT_BASIC_FRAGMENT,
  NOTE_DETAILS_FRAGMENT,
  RiskProfileFragment,
  RiskSectionFragment,
} from '../../graphql/fragments';
import {UpsertRiskNote, UpsertRiskNoteVariables} from '../../schema-types/UpsertRiskNote';
import {
  UpsertRiskNoteSection,
  UpsertRiskNoteSectionVariables,
} from '../../schema-types/UpsertRiskNoteSection';
import {useReviewContext} from '../../contexts/ReviewContext';
import {GetRiskNotes, GetRiskNotesVariables} from '../../schema-types/GetRiskNotes';
import {useFeaturePermission} from '../../contexts/FeatureBoundary';
import {GlobalPermissionEnum, RiskType} from '../../schema-types/globalTypes';
import {UpdateContact, UpdateContactVariables} from '../../schema-types/UpdateContact';
import {UPDATE_CONTACT} from '../../components/AccountOverview/accountOverviewQueries';
import {
  ResyncCuroAtrForRiskNote,
  ResyncCuroAtrForRiskNoteVariables,
} from '../../schema-types/ResyncCuroAtrForRiskNote';
import Loading from '../../components/Loading/Loading';
import {REVIEW_VALIDATION} from '../../pages/HandoverSummaryPage/useReviewValidation';
import validateUUID from '../../utils/validateUUID';

export const GET_RISK_NOTES = gql`
  query GetRiskNotes($reviewId: UUID!) {
    reviewDetails(valueId: $reviewId, keyId: "review_id") {
      id
      dateCreated
      riskProfiles {
        ...RiskProfileFragment
      }
      contacts {
        ...ContactBasicFragment
        giaHasLiquidityNeeds
        pensionHasLiquidityNeeds
        accountContacts {
          id
          account {
            id
            name
          }
        }
      }
    }
  }
  ${RiskProfileFragment}
  ${CONTACT_BASIC_FRAGMENT}
`;

export const GET_RISK_NOTES_AND_NOTES = gql`
  query GetRiskNotesAndNotes($reviewId: UUID!, $accountId: UUID!, $contactId: UUID) {
    reviewDetails(valueId: $reviewId, keyId: "review_id") {
      id
      ...HandoverNotesReviewDetailsFragment
      contacts {
        ...ContactBasicFragment
        giaHasLiquidityNeeds
        pensionHasLiquidityNeeds
        accountContacts {
          id
          account {
            id
            name
          }
        }
      }
      riskProfiles {
        ...RiskProfileFragment
      }
    }
    previousNotes(
      keyId: "account_id"
      valueId: $accountId
      contactKeyId: "contact_id"
      contactValueId: $contactId
    ) {
      ...HandoverNotesReviewDetailsFragment
    }
  }
  ${RiskProfileFragment}
  ${NOTE_DETAILS_FRAGMENT}
  ${CONTACT_BASIC_FRAGMENT}
`;

export const UPSERT_RISK_NOTE = gql`
  mutation UpsertRiskNote($inputs: [RiskProfileInput!]!) {
    upsertRiskProfiles(inputs: $inputs) {
      errors
      ok
      riskProfiles {
        ...RiskProfileFragment
      }
    }
  }
  ${RiskProfileFragment}
`;

export const UPSERT_RISK_NOTE_SECTION = gql`
  mutation UpsertRiskNoteSection($inputs: [RiskProfileSectionInput!]!) {
    upsertRiskProfileSections(inputs: $inputs) {
      errors
      ok
      riskProfileSections {
        ...RiskSectionFragment
      }
    }
  }
  ${RiskSectionFragment}
`;

export const RESYNC_CURO_ATR_FOR_RISK_NOTE = gql`
  mutation ResyncCuroAtrForRiskNote($reviewId: UUID!, $contactId: UUID!) {
    resyncCuroAtrForRiskProfile(reviewId: $reviewId, contactId: $contactId) {
      ok
      errors
    }
  }
`;

export type RiskNotesContextType = {
  upsertRiskProfile: (
    options?: MutationFunctionOptions<UpsertRiskNote, UpsertRiskNoteVariables>
  ) => Promise<FetchResult<UpsertRiskNote>>;
  upsertRiskProfileLoading: boolean;
  upsertRiskProfileError: Error | undefined;
  upsertRiskSection: (
    options?: MutationFunctionOptions<
      UpsertRiskNoteSection,
      UpsertRiskNoteSectionVariables
    >
  ) => Promise<FetchResult<UpsertRiskNoteSection>>;
  upsertRiskSectionLoading: boolean;
  upsertRiskSectionError: Error | undefined;
  riskData: GetRiskNotes | undefined;
  riskLoading: boolean;
  riskError: Error | undefined;
  refetchRiskData: () => Promise<ApolloQueryResult<GetRiskNotes>>;
  saveDummyRiskProfile: (showSuccess: boolean) => Promise<void>;
  resyncCuroAtrForRiskProfileMutation: (
    options?: MutationFunctionOptions<
      ResyncCuroAtrForRiskNote,
      ResyncCuroAtrForRiskNoteVariables
    >
  ) => Promise<FetchResult<ResyncCuroAtrForRiskNote>>;
  resyncCuroAtrForRiskProfileLoading: boolean;
  resyncCuroAtrForRiskProfileError: Error | undefined;
};

export const RiskNotesContext = React.createContext<RiskNotesContextType>({
  upsertRiskProfile: () => Promise.resolve({} as FetchResult<UpsertRiskNote>),
  upsertRiskProfileLoading: false,
  upsertRiskProfileError: undefined,
  upsertRiskSection: () => Promise.resolve({} as FetchResult<UpsertRiskNoteSection>),
  upsertRiskSectionLoading: false,
  upsertRiskSectionError: undefined,
  riskData: undefined,
  riskLoading: false,
  riskError: undefined,
  refetchRiskData: () => Promise.resolve({} as ApolloQueryResult<GetRiskNotes>),
  saveDummyRiskProfile: () => Promise.resolve(),
  resyncCuroAtrForRiskProfileMutation: () =>
    Promise.resolve({} as ApolloQueryResult<ResyncCuroAtrForRiskNote>),
  resyncCuroAtrForRiskProfileLoading: false,
  resyncCuroAtrForRiskProfileError: undefined,
});

// Custom hook that contains all the risk provider logic
// DO NOT IMPORT DIRECTLY
const useRiskNotesProvider = (): RiskNotesContextType => {
  const {reviewId, reviewDetails} = useReviewContext();
  const {enqueueSnackbar} = useSnackbar();
  const isAdmin = useFeaturePermission([GlobalPermissionEnum.can_access_admin_features]);
  const [
    upsertRiskSectionMutation,
    {loading: upsertRiskSectionLoading, error: upsertRiskSectionError},
  ] = useMutation<UpsertRiskNoteSection, UpsertRiskNoteSectionVariables>(
    UPSERT_RISK_NOTE_SECTION,
    {
      refetchQueries: [
        {
          query: REVIEW_VALIDATION,
          variables: {
            valueId: reviewId!,
          },
        },
      ],
      awaitRefetchQueries: true,
    }
  );
  const [
    upsertRiskProfileMutation,
    {loading: upsertRiskProfileLoading, error: upsertRiskProfileError},
  ] = useMutation<UpsertRiskNote, UpsertRiskNoteVariables>(UPSERT_RISK_NOTE, {
    refetchQueries: [
      {
        query: REVIEW_VALIDATION,
        variables: {
          valueId: reviewId!,
        },
      },
    ],
    awaitRefetchQueries: true,
  });
  const [
    resyncCuroAtrForRiskProfileMutation,
    {
      loading: resyncCuroAtrForRiskProfileLoading,
      error: resyncCuroAtrForRiskProfileError,
    },
  ] = useMutation<ResyncCuroAtrForRiskNote, ResyncCuroAtrForRiskNoteVariables>(
    RESYNC_CURO_ATR_FOR_RISK_NOTE
  );
  const [updateContactMutation] = useMutation<UpdateContact, UpdateContactVariables>(
    UPDATE_CONTACT
  );

  const upsertRiskProfile = async (
    options?: MutationFunctionOptions<UpsertRiskNote, UpsertRiskNoteVariables>
  ) => {
    try {
      const result = await upsertRiskProfileMutation({...options});
      if (result.errors?.length) {
        enqueueSnackbar(
          `Error updating risk profile: ${result.errors
            .map((e) => e.message)
            .join(', ')}`,
          {
            variant: 'error',
            persist: true,
          }
        );
      }
      if (result.data?.upsertRiskProfiles?.errors?.length) {
        enqueueSnackbar(
          `Error updating risk profile: ${result.data.upsertRiskProfiles.errors.join(
            ', '
          )}`,
          {
            variant: 'error',
            persist: true,
          }
        );
      }
      return result;
    } catch (error) {
      enqueueSnackbar(
        `Error updating risk profile: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        {
          variant: 'error',
          persist: true,
        }
      );
      throw error;
    }
  };

  const upsertRiskSection = async (
    options?: MutationFunctionOptions<
      UpsertRiskNoteSection,
      UpsertRiskNoteSectionVariables
    >
  ) => {
    const sectionName = options?.variables?.inputs?.[0]?.riskType
      ? `"${options?.variables?.inputs?.[0]?.riskType}`
      : '';
    try {
      const result = await upsertRiskSectionMutation({...options});
      if (result.errors?.length) {
        enqueueSnackbar(
          `Error updating risk section ${sectionName}: ${result.errors
            .map((e) => e.message)
            .join(', ')}`,
          {
            variant: 'error',
            persist: true,
          }
        );
      }
      if (result.data?.upsertRiskProfileSections?.errors?.length) {
        enqueueSnackbar(
          `Error updating risk section ${sectionName}: ${result.data.upsertRiskProfileSections.errors.join(
            ', '
          )}`,
          {
            variant: 'error',
            persist: true,
          }
        );
      }
      return result;
    } catch (error) {
      enqueueSnackbar(
        `Error updating risk section ${sectionName}: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        {
          variant: 'error',
          persist: true,
        }
      );
      throw error;
    }
  };

  const variables: GetRiskNotesVariables = useMemo(
    () => ({
      reviewId: validateUUID(reviewId)!,
    }),
    [reviewId]
  );
  const {
    data: riskData,
    loading: riskLoading,
    error: riskError,
    refetch: _refetchRiskData,
  } = useQuery<GetRiskNotes, GetRiskNotesVariables>(GET_RISK_NOTES, {
    variables,
    fetchPolicy: 'network-only',
    skip: !reviewId,
  });

  const refetchRiskData = () => {
    debugger;
    return _refetchRiskData();
  };

  const saveDummyRiskProfile = async (showSuccess: boolean) => {
    if (!isAdmin) {
      enqueueSnackbar('You do not have permission to create a risk profile', {
        variant: 'error',
        persist: true,
      });
      return;
    }
    try {
      const contacts = riskData?.reviewDetails?.contacts || [];

      if (contacts.length === 0) {
        enqueueSnackbar('No contacts found to create risk profiles for', {
          variant: 'error',
          persist: true,
        });
        return;
      }

      // Create risk profile inputs for each contact with different scores and notes
      const riskProfileInputs = contacts.map((contact, index) => {
        const isFirstContact = index === 0;
        return {
          reviewId: reviewId!,
          contactId: contact.id,
          centralPropositionAgreed: true,
          dfmAgreed: true,
          hasNoRiskDicussion: false,
          isRiskToleranceQuestionnaireDeclined: false,
          riskToleranceQuestionnaireDeclineReason: null,
          noChangeReasonNotes: null,
          outcomeRiskScore: isFirstContact ? 3 : 5, // Different scores for first and second contact
          outcomeRiskNotes: isFirstContact
            ? `Dummy risk profile for ${
                contact.simplifiedName || 'Contact 1'
              } - Conservative approach with moderate risk tolerance.`
            : `Dummy risk profile for ${
                contact.simplifiedName || 'Contact 2'
              } - Aggressive approach with higher risk tolerance.`,
        };
      });

      const result = await upsertRiskProfile({
        variables: {
          inputs: riskProfileInputs,
        },
      });

      if (result.errors?.length) {
        enqueueSnackbar(
          `Error creating dummy risk profile: ${result.errors
            .map((e) => e.message)
            .join(', ')}`,
          {
            variant: 'error',
            persist: true,
          }
        );
        return;
      }
      if (result.data?.upsertRiskProfiles?.errors?.length) {
        enqueueSnackbar(
          `Error creating dummy risk profile: ${result.data.upsertRiskProfiles.errors.join(
            ', '
          )}`,
          {
            variant: 'error',
            persist: true,
          }
        );
        return;
      }

      // Now update risk sections for all contacts
      const allContacts = riskData?.reviewDetails?.contacts || [];
      const isJoint = allContacts.length > 1;
      const updatedSections: string[] = [];

      // Create all section inputs for all contacts in a single mutation
      const allSectionInputs = contacts.flatMap((contact, index) => {
        const capacityScore = isJoint ? (index === 0 ? 1 : 5) : 3;

        // Track what we're updating for the success message
        updatedSections.push(
          `${contact.simplifiedName || 'Contact'} KNOWLEDGE_AND_BEHAVIOUR: dummy text`,
          `${contact.simplifiedName || 'Contact'} VALUES_AND_SERVICE: dummy text`,
          `${contact.simplifiedName || 'Contact'} FINANCIAL_CAPACITY: ${capacityScore}`
        );

        return [
          {
            reviewId: reviewId!,
            contactId: contact.id,
            riskType: RiskType.KNOWLEDGE_AND_BEHAVIOUR,
            score: null,
            notes: 'Dummy knowledge & behaviour assessment for testing purposes.',
            noChange: false,
          },
          {
            reviewId: reviewId!,
            contactId: contact.id,
            riskType: RiskType.VALUES_AND_SERVICE,
            score: null,
            notes:
              'Dummy values & service assessment - client values stability and long-term growth.',
            noChange: false,
          },
          {
            reviewId: reviewId!,
            contactId: contact.id,
            riskType: RiskType.FINANCIAL_CAPACITY,
            score: capacityScore,
            notes: `Dummy financial capacity assessment (${
              isJoint ? 'joint review' : 'single'
            }).`,
            noChange: false,
          },
        ];
      });

      // Execute single mutation with all inputs
      await Promise.all([
        upsertRiskSection({
          variables: {
            inputs: allSectionInputs,
          },
          refetchQueries: [
            {
              query: REVIEW_VALIDATION,
              variables: {
                valueId: reviewId!,
              },
            },
            {
              query: GET_RISK_NOTES,
              variables: {
                reviewId: reviewId!,
              },
            },
          ],
        }),
        ...contacts.map((contact) =>
          updateContactMutation({
            variables: {
              contactId: contact.id,
              giaHasLiquidityNeeds: true,
              pensionHasLiquidityNeeds: true,
            },
          })
        ),
      ]);

      if (showSuccess) {
        enqueueSnackbar(
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{mb: 1}}>
              Dummy risk profiles created successfully for {contacts.length} contact(s)
            </Typography>
            {/*
          <Typography variant="subtitle2" fontWeight="bold" sx={{mb: 0.5}}>
            Risk Profile Values:
          </Typography>
          <List dense sx={{py: 0, mb: 1}}>
            {riskProfileInputs.map((input, index) => (
              <ListItem
                key={`profile-${contacts[index]?.id || index}`}
                sx={{py: 0.5, pl: 2}}
              >
                <ListItemText
                  primary={contacts[index]?.simplifiedName || `Contact ${index + 1}:`}
                  secondary={
                    <Box component="div" sx={{pl: 1, fontSize: '0.8rem'}}>
                      <Typography variant="body2" component="div">
                        • Central Proposition:{' '}
                        {input.centralPropositionAgreed ? 'Agreed' : 'Not Agreed'}
                      </Typography>
                      <Typography variant="body2" component="div">
                        • DFM Agreed: {input.dfmAgreed ? 'Yes' : 'No'}
                      </Typography>
                      <Typography variant="body2" component="div">
                        • Outcome Risk Score: {input.outcomeRiskScore}
                      </Typography>
                      <Typography variant="body2" component="div">
                        • Notes: {input.outcomeRiskNotes}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Typography variant="subtitle2" fontWeight="bold" sx={{mb: 0.5}}>
            Updated Sections:
          </Typography>
          <List dense sx={{py: 0}}>
            {updatedSections.map((section) => (
              <ListItem key={section} sx={{py: 0, pl: 2}}>
                <ListItemText
                  primary={section}
                  primaryTypographyProps={{variant: 'body2'}}
                />
              </ListItem>
            ))}
          </List>

          <Typography variant="subtitle2" fontWeight="bold" sx={{mb: 0.5, mt: 1}}>
            Liquidity Needs:
          </Typography>
          <List dense sx={{py: 0}}>
            {contacts.map((contact) => (
              <ListItem key={`liquidity-${contact.id}`} sx={{py: 0, pl: 2}}>
                <ListItemText
                  primary={`${
                    contact.simplifiedName || 'Contact'
                  }: GIA & Pension liquidity needs set to true`}
                  primaryTypographyProps={{variant: 'body2'}}
                />
              </ListItem>
            ))}
          </List> */}
          </Box>,
          {
            variant: 'success',
            autoHideDuration: 5000,
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        `Error creating dummy risk profile: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        {
          variant: 'error',
          autoHideDuration: 5000,
        }
      );
      throw error;
    }
  };

  return {
    upsertRiskProfile,
    upsertRiskProfileLoading,
    upsertRiskProfileError,
    upsertRiskSection,
    upsertRiskSectionLoading,
    upsertRiskSectionError,
    riskData,
    riskLoading,
    riskError,
    refetchRiskData,
    saveDummyRiskProfile,
    resyncCuroAtrForRiskProfileMutation,
    resyncCuroAtrForRiskProfileLoading,
    resyncCuroAtrForRiskProfileError,
  };
};

export const useRiskNotesExternalOnly = useRiskNotesProvider;

export const RiskNotesProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const contextValue = useRiskNotesProvider();
  if (!contextValue.riskData) {
    return <Loading entityName="Risk" />;
  }
  return (
    <RiskNotesContext.Provider value={contextValue}>{children}</RiskNotesContext.Provider>
  );
};

export const useRiskNotesMutations = () => {
  const context = React.useContext(RiskNotesContext);
  if (!context) {
    throw new Error('useRiskNotesMutations must be used within a RiskNotesProvider');
  }
  return context;
};
